import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Users,
    FileText,
    TrendingUp,
    Eye,
    MousePointer,
    Repeat,
    Share2,
    AlertCircle,
    Download,
    Calendar,
    RefreshCw,
    ArrowLeft,
    BarChart3,
    Activity,
    Target,
    Sparkles,
    Filter,
    UserCheck,
    UserX,
    FlaskConical,
    ArrowRight,
    CheckCircle,
    Info,
    Settings,
    ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Legend
} from 'recharts';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { UsersModal, SessionsModal, BlueprintsModal, ReconciliationModal } from './AdminModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Filter options
const FILTER_OPTIONS = [
    { value: 'all', label: 'All Data', icon: BarChart3 },
    { value: 'exclude_test', label: 'Exclude Test Accounts', icon: UserX },
    { value: 'anonymous_only', label: 'Anonymous Only', icon: Users },
    { value: 'registered_only', label: 'Registered Only', icon: UserCheck }
];

// Beta Summary Card Component
const BetaSummaryCard = ({ data, rawCounts, onTileClick }) => {
    if (!data) return null;
    
    return (
        <Card className="bg-gradient-to-br from-violet-900/30 to-fuchsia-900/30 border-violet-500/30 overflow-hidden">
            <CardHeader className="pb-2 p-3 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-lg">
                    <FlaskConical className="w-4 h-4 text-violet-400 shrink-0" />
                    <span className="truncate">Beta Health Summary</span>
                </CardTitle>
                <CardDescription className="text-xs truncate">Real user activity vs testing (session-based)</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
                <div className="grid grid-cols-2 gap-2">
                    <div 
                        className="p-2 bg-zinc-900/50 rounded-lg cursor-pointer hover:bg-zinc-800/70 transition-colors group"
                        onClick={() => onTileClick?.('users', 'registered')}
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-[10px] text-zinc-400">Real Users</p>
                            <ExternalLink className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-lg font-bold text-green-400">{data.real_registered_users || 0}</p>
                        <p className="text-[9px] text-zinc-500">+{data.new_this_week || 0} this week</p>
                    </div>
                    <div 
                        className="p-2 bg-zinc-900/50 rounded-lg cursor-pointer hover:bg-zinc-800/70 transition-colors group"
                        onClick={() => onTileClick?.('users', 'test')}
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-[10px] text-zinc-400">Test Accounts</p>
                            <ExternalLink className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-lg font-bold text-yellow-400">{data.test_accounts || 0}</p>
                        <p className="text-[9px] text-zinc-500">Excluded</p>
                    </div>
                    <div 
                        className="p-2 bg-zinc-900/50 rounded-lg cursor-pointer hover:bg-zinc-800/70 transition-colors group"
                        onClick={() => onTileClick?.('sessions', 'anonymous')}
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-[10px] text-zinc-400">Anon Sessions</p>
                            <ExternalLink className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-lg font-bold text-blue-400">{data.anonymous_blueprints || 0}</p>
                        <p className="text-[9px] text-zinc-500">with blueprints</p>
                    </div>
                    <div className="p-2 bg-zinc-900/50 rounded-lg">
                        <p className="text-[10px] text-zinc-400">Anon→Signup</p>
                        <p className="text-lg font-bold text-purple-400">{data.anon_to_signup_rate || 0}%</p>
                        <p className="text-[9px] text-zinc-500">Conversion</p>
                    </div>
                </div>
                
                {/* Progress bars */}
                <div className="mt-3 p-2 bg-zinc-900/30 rounded-lg overflow-hidden">
                    <p className="text-[10px] text-zinc-400 mb-2">Sessions with Blueprint Generation</p>
                    <div className="space-y-2">
                        <div 
                            className="cursor-pointer hover:bg-zinc-800/30 rounded p-1 -m-1 transition-colors"
                            onClick={() => onTileClick?.('blueprints', 'anonymous')}
                        >
                            <div className="flex justify-between text-[10px] mb-1">
                                <span className="text-blue-400">Anonymous</span>
                                <span>{data.anonymous_blueprints || 0}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(((data.anonymous_blueprints || 0) / Math.max((data.anonymous_blueprints || 0) + (data.registered_blueprints || 0) + (data.test_blueprints || 0), 1)) * 100, 100)}%` }} />
                            </div>
                        </div>
                        <div 
                            className="cursor-pointer hover:bg-zinc-800/30 rounded p-1 -m-1 transition-colors"
                            onClick={() => onTileClick?.('blueprints', 'registered')}
                        >
                            <div className="flex justify-between text-[10px] mb-1">
                                <span className="text-green-400">Registered</span>
                                <span>{data.registered_blueprints || 0}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.min(((data.registered_blueprints || 0) / Math.max((data.anonymous_blueprints || 0) + (data.registered_blueprints || 0) + (data.test_blueprints || 0), 1)) * 100, 100)}%` }} />
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-[10px] mb-1">
                                <span className="text-yellow-400">Test</span>
                                <span>{data.test_blueprints || 0}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${Math.min(((data.test_blueprints || 0) / Math.max((data.anonymous_blueprints || 0) + (data.registered_blueprints || 0) + (data.test_blueprints || 0), 1)) * 100, 100)}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
                
                {rawCounts && (
                    <div className="mt-2 pt-2 border-t border-zinc-800">
                        <p className="text-[10px] text-zinc-500 mb-1">Raw Event Counts (for reconciliation)</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-zinc-400">
                            <span>Visits: {rawCounts.page_visit_events || 0}</span>
                            <span>Blueprints: {rawCounts.blueprint_generated_events || 0}</span>
                            <span>EMRG: {rawCounts.emrg_click_events || 0}</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = "primary", onClick }) => (
    <Card 
        className={`bg-zinc-900/50 border-zinc-800 ${onClick ? 'cursor-pointer hover:bg-zinc-800/70 transition-colors group' : ''}`}
        onClick={onClick}
    >
        <CardContent className="p-3">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-2">
                    <div className="flex items-center gap-1">
                        <p className="text-[10px] sm:text-xs text-zinc-400 truncate">{title}</p>
                        {onClick && <ExternalLink className="w-2.5 h-2.5 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-white">{value}</p>
                    {subtitle && <p className="text-[10px] text-zinc-500 truncate">{subtitle}</p>}
                </div>
                <div className={`p-1.5 rounded-lg bg-${color}/20`}>
                    <Icon className={`w-3 h-3 sm:w-4 sm:h-4 text-${color}`} />
                </div>
            </div>
        </CardContent>
    </Card>
);

// Conversion Funnel Component
const ConversionFunnel = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    const maxCount = Math.max(...data.map(d => d.count));
    
    return (
        <div className="space-y-3">
            {data.map((stage, index) => {
                const widthPercent = (stage.count / maxCount) * 100;
                const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500'];
                
                return (
                    <div key={stage.stage} className="relative">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-zinc-300">{stage.stage}</span>
                            <span className="text-sm font-medium text-white">{stage.count.toLocaleString()}</span>
                        </div>
                        <div className="h-8 bg-zinc-800 rounded overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${widthPercent}%` }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className={`h-full ${colors[index % colors.length]} rounded flex items-center justify-end pr-2`}
                            >
                                {stage.rate !== undefined && stage.rate < 100 && (
                                    <span className="text-xs font-medium text-white/90">
                                        {stage.rate}%
                                    </span>
                                )}
                            </motion.div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Top Content Table
const TopContentTable = ({ data, title, columns }) => (
    <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
            <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
            {data && data.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-zinc-800">
                                {columns.map(col => (
                                    <th key={col.key} className="text-left py-2 px-3 text-zinc-400 font-medium">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => (
                                <tr key={idx} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                                    {columns.map(col => (
                                        <td key={col.key} className="py-2 px-3 text-zinc-300">
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-zinc-500 text-center py-4">No data available</p>
            )}
        </CardContent>
    </Card>
);

const AdminDashboard = ({ onBack }) => {
    const { getAuthHeaders, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [filterMode, setFilterMode] = useState('exclude_test');
    const [exporting, setExporting] = useState(false);
    
    // Modal states
    const [usersModalOpen, setUsersModalOpen] = useState(false);
    const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
    const [blueprintsModalOpen, setBlueprintsModalOpen] = useState(false);
    const [reconciliationModalOpen, setReconciliationModalOpen] = useState(false);
    const [modalFilter, setModalFilter] = useState(null);

    const fetchData = useCallback(async (filter = filterMode) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(`${API}/admin/summary?filter_mode=${filter}`, {
                headers: getAuthHeaders()
            });
            setData(response.data);
        } catch (err) {
            console.error('Admin data fetch error:', err);
            if (err.response?.status === 403) {
                setError('Access denied. Admin privileges required.');
            } else if (err.response?.status === 401) {
                setError('Please sign in to access the admin dashboard.');
            } else {
                setError('Failed to load dashboard data. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders, filterMode]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (newFilter) => {
        setFilterMode(newFilter);
        fetchData(newFilter);
    };
    
    // Handle tile clicks for drill-down
    const handleTileClick = (type, filter) => {
        setModalFilter(filter);
        if (type === 'users') {
            setUsersModalOpen(true);
        } else if (type === 'sessions') {
            setSessionsModalOpen(true);
        } else if (type === 'blueprints') {
            setBlueprintsModalOpen(true);
        } else if (type === 'reconciliation') {
            setReconciliationModalOpen(true);
        }
    };

    const handleExport = async (format) => {
        setExporting(true);
        try {
            const response = await axios.get(`${API}/admin/export?format=${format}`, {
                headers: getAuthHeaders()
            });
            
            // Create download
            const dataStr = format === 'json' 
                ? JSON.stringify(response.data, null, 2)
                : response.data.data;
            
            const blob = new Blob([dataStr], { 
                type: format === 'json' ? 'application/json' : 'text/csv' 
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vibe-coach-export-${new Date().toISOString().split('T')[0]}.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            toast.success(`Data exported as ${format.toUpperCase()}`);
        } catch (err) {
            toast.error('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-zinc-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400 mb-4">{error}</p>
                    <Button onClick={onBack} variant="outline">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Go Back
                    </Button>
                </div>
            </div>
        );
    }

    const { 
        user_metrics, 
        blueprint_metrics, 
        conversion_metrics, 
        conversion_rates,
        session_linked_emrg,
        funnel,
        content_insights,
        trend_data,
        beta_summary,
        raw_event_counts,
        engagement_metrics,
        filter_mode: currentFilter
    } = data || {};

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4 w-full overflow-hidden"
        >
            {/* Header */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Button
                        data-testid="admin-back-btn"
                        variant="ghost"
                        size="icon"
                        onClick={onBack}
                        className="hover:bg-zinc-800 h-8 w-8 shrink-0"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-primary shrink-0" />
                            <span className="truncate">Admin Dashboard</span>
                        </h1>
                        <p className="text-[10px] sm:text-xs text-zinc-500 truncate">
                            Canonical Beta Metrics • <span className="text-zinc-400">Times shown in Central Time (CT)</span>
                        </p>
                    </div>
                </div>
                
                {/* Controls - Stack on mobile */}
                <div className="space-y-2">
                    {/* Filter dropdown - full width on mobile */}
                    <Select value={filterMode} onValueChange={handleFilterChange}>
                        <SelectTrigger 
                            className="h-8 text-xs bg-zinc-800/50 border-zinc-700 w-full sm:w-48"
                            data-testid="filter-select"
                        >
                            <Filter className="w-3 h-3 mr-1 shrink-0" />
                            <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                            {FILTER_OPTIONS.map(option => (
                                <SelectItem 
                                    key={option.value} 
                                    value={option.value}
                                    className="text-zinc-200 text-xs"
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    {/* Action buttons row */}
                    <div className="flex gap-2">
                        <Button
                            data-testid="refresh-data-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => fetchData()}
                            className="h-8 px-2 bg-zinc-800/50 border-zinc-700 shrink-0"
                        >
                            <RefreshCw className="w-3 h-3" />
                        </Button>
                        <Button
                            data-testid="export-json-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => handleExport('json')}
                            disabled={exporting}
                            className="h-8 px-2 bg-zinc-800/50 border-zinc-700 text-xs shrink-0"
                        >
                            JSON
                        </Button>
                        <Button
                            data-testid="export-csv-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => handleExport('csv')}
                            disabled={exporting}
                            className="h-8 px-2 bg-zinc-800/50 border-zinc-700 text-xs shrink-0"
                        >
                            CSV
                        </Button>
                    </div>
                </div>
            </div>

            {/* Beta Summary Card - Always show */}
            <BetaSummaryCard data={beta_summary} rawCounts={raw_event_counts} onTileClick={handleTileClick} />

            {/* Filter indicator */}
            {filterMode !== 'all' && (
                <div className="flex items-center justify-between text-xs text-zinc-400 bg-zinc-800/30 p-2 rounded-lg">
                    <span>Filter: <span className="text-white">{FILTER_OPTIONS.find(o => o.value === filterMode)?.label}</span></span>
                    <Button variant="ghost" size="sm" onClick={() => handleFilterChange('all')} className="h-6 text-xs px-2">
                        Clear
                    </Button>
                </div>
            )}
            
            {/* Quick Actions Bar */}
            <div className="flex flex-wrap items-center gap-2 p-2 bg-zinc-800/30 rounded-lg overflow-hidden">
                <span className="text-xs text-zinc-400 shrink-0">Quick Actions:</span>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUsersModalOpen(true)}
                        className="h-7 text-xs bg-zinc-800/50 border-zinc-700 whitespace-nowrap"
                    >
                        <Users className="w-3 h-3 mr-1" />
                        Users
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSessionsModalOpen(true)}
                        className="h-7 text-xs bg-zinc-800/50 border-zinc-700 whitespace-nowrap"
                    >
                        <Activity className="w-3 h-3 mr-1" />
                        Sessions
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBlueprintsModalOpen(true)}
                        className="h-7 text-xs bg-zinc-800/50 border-zinc-700 whitespace-nowrap"
                    >
                        <FileText className="w-3 h-3 mr-1" />
                        Blueprints
                    </Button>
                </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-2 gap-2">
                <StatCard
                    title={filterMode === 'anonymous_only' ? "Anonymous Sessions" : "Registered Users"}
                    value={user_metrics?.total_users?.toLocaleString() || 0}
                    subtitle={filterMode === 'anonymous_only' 
                        ? "Non-logged-in visitors" 
                        : `+${user_metrics?.new_users_today || 0} today`}
                    icon={Users}
                    color="blue-500"
                    onClick={() => handleTileClick('users')}
                />
                <StatCard
                    title="Rate Limit Triggers"
                    value={blueprint_metrics?.total_blueprints?.toLocaleString() || 0}
                    subtitle="usage_tracking • click for details"
                    icon={FileText}
                    color="green-500"
                    onClick={() => handleTileClick('reconciliation')}
                />
                <StatCard
                    title="Persisted Blueprints"
                    value={(data?.persisted_blueprints?.total || 0).toLocaleString()}
                    subtitle={`${data?.persisted_blueprints?.saved || 0} saved + ${data?.persisted_blueprints?.shared || 0} shared`}
                    icon={FileText}
                    color="cyan-500"
                    onClick={() => handleTileClick('blueprints')}
                />
                <StatCard
                    title="Page Visits"
                    value={conversion_metrics?.page_visits?.toLocaleString() || 0}
                    subtitle={`${blueprint_metrics?.blueprint_sessions_total || 0} generated blueprints`}
                    icon={Eye}
                    color="purple-500"
                    onClick={() => handleTileClick('sessions')}
                />
                <StatCard
                    title="EMRG Clicks"
                    value={conversion_metrics?.emrg_button_clicks?.toLocaleString() || 0}
                    subtitle={`${conversion_rates?.blueprint_to_emrg || 0}% click-through`}
                    icon={MousePointer}
                    color="yellow-500"
                />
            </div>

            {/* Tabs for different sections */}
            <Tabs defaultValue="overview" className="w-full overflow-hidden">
                <TabsList className="bg-zinc-900 border border-zinc-800 w-full grid grid-cols-4">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-800 text-[10px] sm:text-sm px-1 sm:px-3">
                        Overview
                    </TabsTrigger>
                    <TabsTrigger value="engagement" className="data-[state=active]:bg-zinc-800 text-[10px] sm:text-sm px-1 sm:px-3">
                        Engage
                    </TabsTrigger>
                    <TabsTrigger value="users" className="data-[state=active]:bg-zinc-800 text-[10px] sm:text-sm px-1 sm:px-3">
                        Users
                    </TabsTrigger>
                    <TabsTrigger value="funnel" className="data-[state=active]:bg-zinc-800 text-[10px] sm:text-sm px-1 sm:px-3">
                        Funnel
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                    {/* Trend Chart */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader className="p-3 sm:p-6 pb-0">
                            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                                <Activity className="w-4 h-4 text-primary" />
                                Activity Trends
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 sm:p-6">
                            <div className="h-[200px] sm:h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={trend_data}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                                        <XAxis 
                                            dataKey="date" 
                                            stroke="#71717a"
                                            tick={{ fill: '#a1a1aa', fontSize: 10 }}
                                            tickFormatter={(val) => val.split('-').slice(1).join('/')}
                                        />
                                        <YAxis stroke="#71717a" tick={{ fill: '#a1a1aa', fontSize: 10 }} width={30} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#18181b', 
                                                border: '1px solid #27272a',
                                                borderRadius: '8px',
                                                fontSize: '12px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                                        <Line type="monotone" dataKey="visits" stroke="#8b5cf6" strokeWidth={2} name="Visits" dot={false} />
                                        <Line type="monotone" dataKey="blueprints" stroke="#22c55e" strokeWidth={2} name="Blueprints" dot={false} />
                                        <Line type="monotone" dataKey="emrg_clicks" stroke="#eab308" strokeWidth={2} name="EMRG" dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Conversion Rates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Target className="w-5 h-5 text-green-500" />
                                    Conversion Rates (Session-Based)
                                </CardTitle>
                                <CardDescription>Unique sessions that performed each action</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Visit Session → Blueprint Session</span>
                                    <span className="text-xl font-bold text-green-400">
                                        {conversion_rates?.visit_to_blueprint || 0}%
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Blueprint Session → EMRG Session</span>
                                    <span className="text-xl font-bold text-yellow-400">
                                        {conversion_rates?.blueprint_to_emrg || 0}%
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Blueprint Session → Remix Session</span>
                                    <span className="text-xl font-bold text-purple-400">
                                        {conversion_rates?.blueprint_to_remix || 0}%
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Shared View → Action</span>
                                    <span className="text-xl font-bold text-blue-400">
                                        {conversion_rates?.shared_to_action || 0}%
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-blue-500" />
                                    Blueprint Stats
                                </CardTitle>
                                <CardDescription>
                                    {filterMode === 'exclude_test' && 'Excluding test accounts'}
                                    {filterMode === 'anonymous_only' && 'Anonymous sessions only'}
                                    {filterMode === 'registered_only' && 'Registered users only'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Avg per Registered User</span>
                                    <span className="text-xl font-bold text-white">
                                        {blueprint_metrics?.avg_per_user || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">This Week</span>
                                    <span className="text-xl font-bold text-white">
                                        {blueprint_metrics?.blueprints_week?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">This Month</span>
                                    <span className="text-xl font-bold text-white">
                                        {blueprint_metrics?.blueprints_month?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400">Users at Daily Limit</span>
                                    <span className="text-xl font-bold text-orange-400">
                                        {blueprint_metrics?.users_at_limit_today || 0}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Engagement Tab - Repeat User Analytics */}
                <TabsContent value="engagement" className="space-y-6 mt-6">
                    {/* Key Engagement Metrics */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            title="Returning Sessions"
                            value={`${engagement_metrics?.returning_pct || 0}%`}
                            subtitle={`${engagement_metrics?.returning_sessions || 0} of ${engagement_metrics?.total_sessions || 0}`}
                            icon={Repeat}
                            color="green-500"
                        />
                        <StatCard
                            title="Sessions/User"
                            value={engagement_metrics?.avg_sessions_per_user || 0}
                            subtitle="Avg sessions per registered user"
                            icon={Users}
                            color="blue-500"
                        />
                        <StatCard
                            title="Blueprints/Session"
                            value={engagement_metrics?.avg_blueprints_per_session || 0}
                            subtitle={`${engagement_metrics?.sessions_with_blueprints || 0} sessions with blueprints`}
                            icon={FileText}
                            color="purple-500"
                        />
                        <StatCard
                            title="Blueprints/User"
                            value={engagement_metrics?.avg_blueprints_per_user || 0}
                            subtitle="Avg per registered user"
                            icon={FileText}
                            color="yellow-500"
                        />
                    </div>

                    {/* New vs Returning Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-500" />
                                    New vs Returning Sessions
                                </CardTitle>
                                <CardDescription>Session breakdown by visitor type</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-blue-400">New Sessions</span>
                                            <span className="text-white font-medium">{engagement_metrics?.new_sessions || 0}</span>
                                        </div>
                                        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-blue-500 rounded-full transition-all"
                                                style={{ width: `${100 - (engagement_metrics?.returning_pct || 0)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-green-400">Returning Sessions</span>
                                            <span className="text-white font-medium">{engagement_metrics?.returning_sessions || 0}</span>
                                        </div>
                                        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-green-500 rounded-full transition-all"
                                                style={{ width: `${engagement_metrics?.returning_pct || 0}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center pt-2 border-t border-zinc-800">
                                    <span className="text-2xl font-bold text-green-400">{engagement_metrics?.returning_pct || 0}%</span>
                                    <span className="text-zinc-400 ml-2">returning visitor rate</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Repeat className="w-5 h-5 text-purple-500" />
                                    Repeat User Behavior
                                </CardTitle>
                                <CardDescription>Engagement metrics for returning users</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <span className="text-zinc-400">Total Returning Users</span>
                                    <span className="text-xl font-bold text-white">{engagement_metrics?.total_returning_users || 0}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <span className="text-zinc-400">Avg Sessions/Returning User</span>
                                    <span className="text-xl font-bold text-green-400">{engagement_metrics?.avg_sessions_per_returning_user || 0}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <span className="text-zinc-400">Avg Blueprints/Returning User</span>
                                    <span className="text-xl font-bold text-purple-400">{engagement_metrics?.avg_blueprints_per_returning_user || 0}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Retention Timeline */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-yellow-500" />
                                Retention Timeline
                            </CardTitle>
                            <CardDescription>How many users return after their first visit</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400 mb-2">Next Day</p>
                                    <p className="text-3xl font-bold text-blue-400">{engagement_metrics?.retention?.next_day_pct || 0}%</p>
                                    <p className="text-xs text-zinc-500 mt-1">{engagement_metrics?.retention?.users_returned_next_day || 0} users</p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400 mb-2">Within 7 Days</p>
                                    <p className="text-3xl font-bold text-green-400">{engagement_metrics?.retention?.seven_day_pct || 0}%</p>
                                    <p className="text-xs text-zinc-500 mt-1">{engagement_metrics?.retention?.users_returned_7_days || 0} users</p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400 mb-2">Within 30 Days</p>
                                    <p className="text-3xl font-bold text-purple-400">{engagement_metrics?.retention?.thirty_day_pct || 0}%</p>
                                    <p className="text-xs text-zinc-500 mt-1">{engagement_metrics?.retention?.users_returned_30_days || 0} users</p>
                                </div>
                            </div>
                            <p className="text-xs text-zinc-500 text-center mt-4">Based on {engagement_metrics?.retention?.total_users_tracked || 0} registered users tracked</p>
                        </CardContent>
                    </Card>

                    {/* Returning User Engagement Funnel */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="w-5 h-5 text-green-500" />
                                Returning User Engagement Funnel
                            </CardTitle>
                            <CardDescription>Conversion rates specifically for returning users</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Eye className="w-5 h-5 text-blue-400" />
                                        <span className="text-zinc-300">Visit Sessions</span>
                                    </div>
                                    <span className="font-bold text-white">{engagement_metrics?.returning_funnel?.visit_sessions || 0}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-green-400" />
                                        <span className="text-zinc-300">Blueprint Sessions</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-bold text-white">{engagement_metrics?.returning_funnel?.blueprint_sessions || 0}</span>
                                        <span className="text-green-400 text-sm ml-2">({engagement_metrics?.returning_funnel?.visit_to_blueprint_rate || 0}%)</span>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <MousePointer className="w-5 h-5 text-yellow-400" />
                                        <span className="text-zinc-300">EMRG Click Sessions</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-bold text-white">{engagement_metrics?.returning_funnel?.emrg_sessions || 0}</span>
                                        <span className="text-yellow-400 text-sm ml-2">({engagement_metrics?.returning_funnel?.blueprint_to_emrg_rate || 0}%)</span>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Repeat className="w-5 h-5 text-purple-400" />
                                        <span className="text-zinc-300">Remix Sessions</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-bold text-white">{engagement_metrics?.returning_funnel?.remix_sessions || 0}</span>
                                        <span className="text-purple-400 text-sm ml-2">({engagement_metrics?.returning_funnel?.blueprint_to_remix_rate || 0}%)</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Insight Summary */}
                    <Card className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border-emerald-500/30">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Sparkles className="w-5 h-5 text-emerald-400" />
                                Engagement Insights
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="p-3 bg-zinc-900/50 rounded-lg">
                                    <p className="text-zinc-400 mb-1">User Engagement Pattern</p>
                                    <p className="text-white">
                                        {(engagement_metrics?.avg_blueprints_per_session || 0) > 1 
                                            ? `Users experiment with ${engagement_metrics?.avg_blueprints_per_session} ideas per session on average`
                                            : 'Users typically generate 1 blueprint per session'
                                        }
                                    </p>
                                </div>
                                <div className="p-3 bg-zinc-900/50 rounded-lg">
                                    <p className="text-zinc-400 mb-1">Return Behavior</p>
                                    <p className="text-white">
                                        {(engagement_metrics?.returning_pct || 0) > 20 
                                            ? `${engagement_metrics?.returning_pct}% of sessions are from returning visitors - good engagement!`
                                            : `${engagement_metrics?.returning_pct}% returning rate - focus on retention`
                                        }
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Users Tab */}
                <TabsContent value="users" className="space-y-6 mt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            title="DAS"
                            value={user_metrics?.dau?.toLocaleString() || 0}
                            subtitle="Daily Active Sessions"
                            icon={Users}
                            color="blue-500"
                        />
                        <StatCard
                            title="WAS"
                            value={user_metrics?.wau?.toLocaleString() || 0}
                            subtitle="Weekly Active Sessions"
                            icon={Users}
                            color="green-500"
                        />
                        <StatCard
                            title="MAS"
                            value={user_metrics?.mau?.toLocaleString() || 0}
                            subtitle="Monthly Active Sessions"
                            icon={Users}
                            color="purple-500"
                        />
                        <StatCard
                            title="New This Week"
                            value={user_metrics?.new_users_week?.toLocaleString() || 0}
                            subtitle={`${user_metrics?.new_users_month || 0} this month`}
                            icon={TrendingUp}
                            color="yellow-500"
                        />
                    </div>

                    {/* New Users Trend */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle>New User Signups (Last 14 Days)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[250px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={trend_data}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                                        <XAxis 
                                            dataKey="date" 
                                            stroke="#71717a"
                                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                                            tickFormatter={(val) => val.split('-').slice(1).join('/')}
                                        />
                                        <YAxis stroke="#71717a" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#18181b', 
                                                border: '1px solid #27272a',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Bar dataKey="new_users" fill="#8b5cf6" name="New Users" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Returning vs New */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle>Visitor Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-8">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-zinc-400">New Visitors</span>
                                        <span className="font-bold text-white">
                                            {user_metrics?.new_visitors?.toLocaleString() || 0}
                                        </span>
                                    </div>
                                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-blue-500 rounded-full"
                                            style={{ 
                                                width: `${((user_metrics?.new_visitors || 0) / ((user_metrics?.new_visitors || 0) + (user_metrics?.returning_users || 0) || 1)) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-zinc-400">Returning Visitors</span>
                                        <span className="font-bold text-white">
                                            {user_metrics?.returning_users?.toLocaleString() || 0}
                                        </span>
                                    </div>
                                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-green-500 rounded-full"
                                            style={{ 
                                                width: `${((user_metrics?.returning_users || 0) / ((user_metrics?.new_visitors || 0) + (user_metrics?.returning_users || 0) || 1)) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Content Tab */}
                <TabsContent value="content" className="space-y-6 mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TopContentTable
                            title="Top Viewed Blueprints"
                            data={content_insights?.top_viewed_blueprints || []}
                            columns={[
                                { key: 'app_name', label: 'App Name' },
                                { key: 'idea', label: 'Idea', render: (val) => val?.substring(0, 40) + '...' },
                                { key: 'views', label: 'Views', render: (val) => val?.toLocaleString() }
                            ]}
                        />
                        
                        <TopContentTable
                            title="Most Popular App Types"
                            data={content_insights?.top_app_names || []}
                            columns={[
                                { key: 'name', label: 'App Name' },
                                { key: 'count', label: 'Count', render: (val) => val?.toLocaleString() }
                            ]}
                        />
                    </div>

                    <TopContentTable
                        title="Most Remixed Ideas"
                        data={content_insights?.top_remixed || []}
                        columns={[
                            { key: 'idea', label: 'Original Idea' },
                            { key: 'count', label: 'Remix Count', render: (val) => val?.toLocaleString() }
                        ]}
                    />
                </TabsContent>

                {/* Funnel Tab */}
                <TabsContent value="funnel" className="space-y-6 mt-6">
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="w-5 h-5 text-primary" />
                                Conversion Funnel
                            </CardTitle>
                            <CardDescription>
                                User journey from page visit to engagement
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ConversionFunnel data={funnel} />
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle>Funnel Analysis</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="p-4 bg-zinc-800/50 rounded-lg">
                                    <p className="text-sm text-zinc-400 mb-2">Biggest Drop-off</p>
                                    <p className="text-lg font-medium text-white">
                                        Page Visit → Blueprint Generation
                                    </p>
                                    <p className="text-sm text-zinc-500">
                                        {100 - (conversion_rates?.visit_to_blueprint || 0)}% of visitors leave without generating
                                    </p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg">
                                    <p className="text-sm text-zinc-400 mb-2">Best Conversion</p>
                                    <p className="text-lg font-medium text-white">
                                        {conversion_rates?.blueprint_to_emrg > conversion_rates?.blueprint_to_remix 
                                            ? 'Blueprint → EMRG Click' 
                                            : 'Blueprint → Remix'}
                                    </p>
                                    <p className="text-sm text-zinc-500">
                                        {Math.max(conversion_rates?.blueprint_to_emrg || 0, conversion_rates?.blueprint_to_remix || 0)}% conversion rate
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardHeader>
                                <CardTitle>Key Actions</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Share2 className="w-5 h-5 text-blue-400" />
                                            <span className="text-zinc-300">Shared Views</span>
                                        </div>
                                        <span className="font-bold text-white">
                                            {conversion_metrics?.shared_blueprint_views?.toLocaleString() || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Repeat className="w-5 h-5 text-purple-400" />
                                            <span className="text-zinc-300">Remixes</span>
                                        </div>
                                        <span className="font-bold text-white">
                                            {conversion_metrics?.blueprint_remixes?.toLocaleString() || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <MousePointer className="w-5 h-5 text-yellow-400" />
                                            <span className="text-zinc-300">EMRG Clicks</span>
                                        </div>
                                        <span className="font-bold text-white">
                                            {conversion_metrics?.emrg_button_clicks?.toLocaleString() || 0}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Session-Linked EMRG Tracking */}
                    <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-yellow-500">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MousePointer className="w-5 h-5 text-yellow-400" />
                                Session-Linked EMRG Tracking
                            </CardTitle>
                            <CardDescription>
                                EMRG clicks traced back to blueprint sessions (regardless of calendar day)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-white">
                                        {session_linked_emrg?.total_blueprint_sessions?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-zinc-400">Blueprint Sessions</p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-yellow-400">
                                        {session_linked_emrg?.emrg_sessions_from_blueprint?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-zinc-400">With EMRG Click</p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-green-400">
                                        {session_linked_emrg?.true_funnel_conversion_rate || 0}%
                                    </p>
                                    <p className="text-xs text-zinc-400">True Funnel Rate</p>
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-zinc-500">
                                        {session_linked_emrg?.emrg_sessions_not_from_blueprint?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-zinc-400">Orphan EMRG</p>
                                </div>
                            </div>
                            <div className="mt-4 p-3 bg-zinc-800/30 rounded-lg">
                                <p className="text-xs text-zinc-500">
                                    <strong>True Funnel Rate:</strong> % of sessions that generated a blueprint AND later clicked EMRG (any day).
                                    <br />
                                    <strong>Orphan EMRG:</strong> EMRG clicks from sessions without any blueprint generation.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Footer Info */}
            <div className="text-center text-xs text-zinc-600 py-4">
                Last updated: {data?.generated_at ? new Date(data.generated_at).toLocaleString() : 'N/A'}
            </div>
            
            {/* Admin Modals */}
            <UsersModal 
                isOpen={usersModalOpen} 
                onClose={() => setUsersModalOpen(false)} 
                getAuthHeaders={getAuthHeaders}
            />
            <SessionsModal 
                isOpen={sessionsModalOpen} 
                onClose={() => setSessionsModalOpen(false)} 
                getAuthHeaders={getAuthHeaders}
                initialFilter={modalFilter}
            />
            <BlueprintsModal 
                isOpen={blueprintsModalOpen} 
                onClose={() => setBlueprintsModalOpen(false)} 
                getAuthHeaders={getAuthHeaders}
                initialFilter={modalFilter}
            />
            <ReconciliationModal
                isOpen={reconciliationModalOpen}
                onClose={() => setReconciliationModalOpen(false)}
                getAuthHeaders={getAuthHeaders}
                filterMode={filterMode}
            />
        </motion.div>
    );
};

export default AdminDashboard;
